import { Ace, Range } from 'ace-builds'
import { useCallback, useEffect, useRef, useState } from 'react'
import AceEditor from 'react-ace'

import { useTheme } from '@mui/material/styles'
import 'ace-builds/src-noconflict/ace'
import 'ace-builds/src-noconflict/keybinding-emacs'
import 'ace-builds/src-noconflict/keybinding-vim'
import 'ace-builds/src-noconflict/mode-coffee'
import 'ace-builds/src-noconflict/mode-yaml'
import 'ace-builds/src-noconflict/theme-dawn'
import 'ace-builds/src-noconflict/theme-monokai'

import ControlPanel from './ControlPanel'

import { Box } from '@mui/material'
import './Editor.css'
import styles from './Editor.module.css'

/**
 * @type {Problem}
 */
export type Problem = {
  line: number
  what: string
}

/**
 * @type {onFineshedFunc}
 */
export type OnFinishedFunc = (lints: Problem[], errors: Problem[]) => void
/**
 * @type {OnErrorFunc}
 */
export type OnErrorFunc = (err: Error) => void

/**
 * @typedef {interface}
 * @prop {string} name
 * @prop {string} contents
 * @prop {Array<Problem>} [initWarnings]
 * @prop {Array<Problem>} [initErrors]
 * @prop {string} mode
 * @prop {boolean} readOnly
 * @prop {callback} onSave
 * @prop {callback} onChange
 * @prop {boolean} [hideControls]
 * @prop {boolean} [showGutter]
 */
interface AppParams {
  name: string
  contents: string
  initWarnings?: Problem[]
  initErrors?: Problem[]
  mode: string // TODO: Enum
  readOnly: boolean
  onSave: (saveConfig: string | null, onFinished: OnFinishedFunc, onError: OnErrorFunc) => void
  onChange: (contents: string, event?: any) => void
  hideControls?: boolean
  showGutter?: boolean
}

/**
 * @type {contentsUpdates}
 */
type contentsUpdates = {
  isDirty: boolean
  newContents: string | null
}

/**
 * file editor - bloblang file or yaml file
 * @returns {JSX.Element}
 */
export default function App({
  name = 'bstdio_editor',
  contents,
  initWarnings,
  initErrors,
  mode,
  readOnly,
  onSave,
  onChange,
  hideControls = false,
  showGutter = true,
}: AppParams) {
  const theme = useTheme()

  const [saveErrored, setSaveErrored] = useState(false)
  const editorRef = useRef<AceEditor>(null)

  const savePeriod = 1000

  const contentsUpdatesRef = useRef<contentsUpdates>({
    isDirty: false,
    newContents: null,
  })
  const onContentChange = useCallback(
    (contents: string, event?: any) => {
      contentsUpdatesRef.current.isDirty = true
      contentsUpdatesRef.current.newContents = contents
      onChange(contents, event)
    },
    [onChange]
  )

  const undo = useCallback(() => {
    const eSession = editorRef.current?.editor.getSession()
    eSession?.getUndoManager().undo(eSession)
  }, [editorRef])

  const redo = useCallback(() => {
    const eSession = editorRef.current?.editor.getSession()
    eSession?.getUndoManager().redo(eSession)
  }, [editorRef])

  const annotations = useCallback(
    (warnings: Problem[], errors: Problem[]) => {
      if (editorRef.current === null) {
        return
      }

      const session = editorRef.current.editor.getSession()

      const prevMarkers = session.getMarkers()
      Object.keys(prevMarkers).forEach((id: any) => {
        // NOTE: Something seems off about the annotations from the markers
        if (prevMarkers[id].clazz === 'warningMarker') {
          session.removeMarker(id)
        } else if (prevMarkers[id].clazz === 'errorMarker') {
          session.removeMarker(id)
        }
      })

      const annotations: Ace.Annotation[] = []
      ;(warnings || []).forEach((err: Problem) => {
        session.addMarker(new Range(err.line - 1, 0, err.line - 1, 1), 'warningMarker', 'fullLine')
        annotations.push({
          row: err.line - 1,
          text: err.what,
          type: 'warning',
          column: 0
        })
      })
      ;(errors || []).forEach((err: Problem) => {
        session.addMarker(new Range(err.line - 1, 0, err.line - 1, 1), 'errorMarker', 'fullLine')
        annotations.push({
          row: err.line - 1,
          text: err.what,
          type: 'error',
          column: 0
        })
      })
      session.setAnnotations(annotations)
    },
    [editorRef]
  )

  useEffect(() => {
    annotations(initWarnings || [], initErrors || [])
  }, [initErrors, initWarnings, annotations])

  const saveContents = useCallback(
    (onDone: () => void) => {
      const updateRef = contentsUpdatesRef.current
      const saveContents = updateRef.newContents

      if (!updateRef.isDirty) {
        if (onDone) {
          onDone()
        }
        return
      }

      let subscribed = true

      onSave(
        saveContents,
        (lints, errors) => {
          onDone()
          if (!subscribed) {
            return
          }
          if (updateRef.newContents === saveContents) {
            updateRef.isDirty = false
            updateRef.newContents = null
            setSaveErrored(false)
          }
          annotations(lints, errors)
        },
        () => {
          onDone()
          if (!subscribed) {
            return
          }
          setSaveErrored(true)
        }
      )

      return () => {
        subscribed = false
      }
    },
    [annotations, onSave]
  )

  // This is required, otherwise when the contents are changed for another file
  // the markers and annotations aren't updated to the new lints and errors.
  //
  // It's also important to do things like reset the undo stack so that file
  // sessions don't bleed into each other.
  useEffect(() => {
    let pending = false

    if (editorRef.current !== null) {
      const session = editorRef.current.editor.getSession()
      session.getUndoManager().reset()
    }
    if (contentsUpdatesRef !== null) {
      contentsUpdatesRef.current = { isDirty: false, newContents: null }
    }

    const saveInterval = setInterval(() => {
      if (pending) {
        return
      }
      pending = true
      saveContents(() => {
        pending = false
      })
    }, savePeriod)

    return function cleanup() {
      clearInterval(saveInterval)
      saveContents(() => {
        pending = false
      })
    }
  }, [editorRef, saveContents])
  const value = contentsUpdatesRef.current?.newContents !== null ? contentsUpdatesRef.current?.newContents : contents
  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <AceEditor
        mode={mode}
        ref={editorRef}
        markers={[{
          startRow: 0, startCol: 2, endRow: 1, endCol: 20, className: 'error-marker',
          type: 'text'
        }]}
        theme={theme.palette.mode === 'dark' ? 'monokai' : 'dawn'}
        value={value}
        onChange={onContentChange}
        fontSize="14px"
        name={name}
        width={'100%'}
        height={'100%'}
        showGutter={showGutter}
        editorProps={{
          $blockScrolling: true,
        }}
        readOnly={false}
        showPrintMargin={false}
        commands={[
          {
            name: 'saveCommand',
            bindKey: { win: 'Ctrl-s', mac: 'Command-s' },
            exec: () => saveContents(() => {}),
          },
        ]}
      ></AceEditor>
      {hideControls ? null : (
        <div className={styles.controlPanel} style={{ background: theme.palette.primary.main }}>
          <ControlPanel
            canUndo={editorRef.current?.editor.getSession().getUndoManager().canUndo() || false}
            canRedo={editorRef.current?.editor.getSession().getUndoManager().canRedo() || false}
            onUndo={undo}
            onRedo={redo}
          ></ControlPanel>
        </div>
      )}
      {readOnly && !hideControls && !saveErrored ? (
        <div className={styles.readOnlyWarning}>
          <p>
            You only have permission to view this document as a read only copy. In order to modify it copy the contents into a file within a
            session that you own.
          </p>
        </div>
      ) : null}
      {saveErrored ? (
        <div className={styles.saveErrorWarning}>
          <p>
            Failed to sync contents with session. If this problem persists then it is recommended that you copy and save the contents
            locally and then refresh the page.
          </p>
        </div>
      ) : null}
    </Box>
  )
}
